import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { EnterpriseScreenShell, actionButtonFactory, useStandardConfirms, type FormModalMode } from 'components/enterprise-shell';
import type { ActionButton } from 'components/modals';
import { SyutGrid, SyutSelect, type ActionMenuItem, type SyutGridColumn } from 'components/ui';
import GridLoadingSkeleton from 'components/common/GridLoadingSkeleton';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useDefaultProperty } from 'hooks/api/useDefaultProperty';
import notificationService from 'services/notificationService';
import { dineOpsService } from 'api/services/dineOpsService';
import { propertyService } from 'api/services';
import { downloadCsv } from 'utils/csvExport';
import type { DineKitchen } from 'api/types';
import DineOpsKitchenFormModal, { type PropertyOption } from './DineOpsKitchenFormModal';
import DineOpsKitchenViewModal from './DineOpsKitchenViewModal';

/**
 * ============================================================================
 * KITCHENS (SETUP-009) — where kitchen stations are set up
 * ============================================================================
 * A kitchen (Main Kitchen, Bar, Pantry) and its stations, each kitchen linked to
 * an organisation unit (usually the F&B department). A tenant master: it never
 * depends on the outlet in the header; the Property filter (default: the user's
 * default property) shows that property's kitchens and the shared ones. Day-to-day
 * opening and closing of stations is on Kitchen Stations.
 * ============================================================================
 */

interface Props {
  tabId?: string;
  moduleIcon?: string;
  moduleName?: string;
  allowedActions?: string[];
}

/** propertyId null = not chosen yet: the user's default property applies. */
interface Filters { propertyId: string | null; status: '' | 'all' }

const DineOpsKitchensScreen: React.FC<Props> = ({ tabId, moduleName = 'Kitchens', allowedActions = [] }) => {
  const canAdd = allowedActions.includes('add') || allowedActions.includes('create');
  const canEdit = allowedActions.includes('edit') || allowedActions.includes('update');
  const canDelete = allowedActions.includes('delete');

  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();
  const { confirmDelete, ConfirmDialogPortal } = useStandardConfirms();
  const { defaultPropertyId, loading: defaultLoading } = useDefaultProperty();

  // ---- properties (filter and form) ---------------------------------------------------------
  const [properties, setProperties] = useState<PropertyOption[]>([]);
  useEffect(() => {
    propertyService.getAll({ page: 1, pageSize: 500, includeInactive: false })
      .then((res) => setProperties((res.data ?? []).map((p: any) => ({ id: p.id, name: p.propertyName, entityId: p.entityId ?? undefined }))))
      .catch(() => setProperties([]));
  }, []);

  // ---- filters, search and the server-side list ---------------------------------------------
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState<Filters>({ propertyId: null, status: '' });
  const [draft, setDraft] = useState<Filters>({ propertyId: null, status: '' });
  // The property in force: the user's choice, else the default. Never copied into state when the
  // default arrives, which would load the list twice; the first load waits for the default instead.
  const propertyId = filters.propertyId ?? defaultPropertyId;
  const draftPropertyId = draft.propertyId ?? defaultPropertyId;
  const waitForDefault = filters.propertyId === null && defaultLoading;

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => { const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300); return () => clearTimeout(t); }, [searchTerm]);

  const [rows, setRows] = useState<DineKitchen[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);

  const loadSeq = useRef(0);
  const load = useCallback(async () => {
    if (waitForDefault) return;
    const seq = ++loadSeq.current;   // a reply to an older request never overwrites a newer one
    setLoading(true);
    try {
      const res = await dineOpsService.getKitchens({
        page, pageSize, searchTerm: debouncedSearch || undefined,
        propertyId: propertyId || undefined, includeInactive: filters.status === 'all',
      });
      if (seq !== loadSeq.current) return;
      setRows(res.data);
      setTotalCount(res.pagination.totalItems);
    } catch (err: any) {
      if (seq === loadSeq.current) notificationService.toast.error(err?.response?.data?.message || err?.message || 'The kitchens could not be loaded.');
    } finally {
      if (seq !== loadSeq.current) return;
      setLoading(false);
      setReady(true);
    }
  }, [page, pageSize, debouncedSearch, propertyId, filters.status, waitForDefault]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { setPage(1); }, [debouncedSearch, propertyId, filters.status]);

  const appliedCount = (propertyId ? 1 : 0) + (filters.status ? 1 : 0);

  // ---- form and view ------------------------------------------------------------------------
  const [editor, setEditor] = useState<{ mode: FormModalMode; record: DineKitchen | null } | null>(null);
  const [viewing, setViewing] = useState<DineKitchen | null>(null);
  const [createInstanceId, setCreateInstanceId] = useState('1');

  const restoreIfMinimized = useCallback((modalId: string | null): boolean => {
    if (!modalId || !isMinimized(modalId)) return false;
    const modal = getMinimizedModal(modalId);
    dismissMinimized(modalId);
    modal?.onRestore?.();
    return true;
  }, [isMinimized, getMinimizedModal, dismissMinimized]);

  /** Edit, copy and view need the kitchen's stations: always read it fresh. */
  const loadFull = useCallback(async (id: string): Promise<DineKitchen | null> => {
    try { return await dineOpsService.getKitchen(id); } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'The kitchen could not be loaded. Refresh and try again.');
      return null;
    }
  }, []);

  const openCreate = useCallback(() => {
    let next = 1;
    while (tabId && isMinimized(`${tabId}-form-modal-create-${next}`)) next += 1;
    setCreateInstanceId(String(next));
    setEditor({ mode: 'create', record: null });
  }, [tabId, isMinimized]);

  const openFor = useCallback(async (mode: FormModalMode, row: DineKitchen) => {
    if (restoreIfMinimized(tabId ? `${tabId}-form-modal-${mode}-${row.id}` : null)) return;
    const full = await loadFull(row.id);
    if (full) { setViewing(null); setEditor({ mode, record: full }); }
  }, [tabId, restoreIfMinimized, loadFull]);

  const openView = useCallback(async (row: DineKitchen) => {
    if (restoreIfMinimized(tabId ? `${tabId}-view-modal-${row.id}` : null)) return;
    const full = await loadFull(row.id);
    if (full) setViewing(full);
  }, [tabId, restoreIfMinimized, loadFull]);

  const handleRestoreModal = useCallback(async (mode: FormModalMode, instanceId?: string, recordId?: string) => {
    if (instanceId) setCreateInstanceId(instanceId);
    if (mode === 'create' || !recordId) { setEditor({ mode, record: null }); return; }
    const full = await loadFull(recordId);
    if (full) setEditor({ mode, record: full });
  }, [loadFull]);

  const handleRestoreView = useCallback(async (recordId: string) => {
    const full = await loadFull(recordId);
    if (full) setViewing(full);
  }, [loadFull]);

  const handleDelete = useCallback(async (record: DineKitchen) => {
    const ok = await confirmDelete({
      resourceLabel: `kitchen ${record.kitchenCode} — ${record.kitchenName}`,
      message: record.stationCount > 0
        ? `${record.kitchenName} still has ${record.stationCount} station(s). Move them to another kitchen first (Edit → remove the rows), or make the kitchen inactive.`
        : `Delete ${record.kitchenName}? This cannot be undone.`,
    });
    if (!ok) return;
    try {
      await dineOpsService.deleteKitchen(record.id, record.rowVersion);
      notificationService.toast.success(`${record.kitchenName} deleted.`);
      setViewing(null);
      void load();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || err?.message || 'Delete failed.');
    }
  }, [confirmDelete, load]);

  const handleExport = useCallback(() => {
    if (rows.length === 0) { notificationService.toast.warning('No data to export'); return; }
    downloadCsv(`kitchens_${new Date().toISOString().split('T')[0]}.csv`,
      ['Code', 'Kitchen', 'Property', 'Business Unit', 'Stations', 'Station names', 'Status'],
      rows.map((r) => [r.kitchenCode, r.kitchenName, r.propertyName ?? 'Shared', r.businessUnitName, r.stationCount, r.stationNames, r.isActive ? 'Active' : 'Inactive']));
  }, [rows]);

  // ---- grid ---------------------------------------------------------------------------------
  const columns = useMemo<SyutGridColumn<DineKitchen>[]>(() => [
    { id: 'kitchenCode', header: 'Code', accessorKey: 'kitchenCode', size: 120,
      cell: ({ row }) => <span style={{ fontFamily: 'monospace' }}>{row.original.kitchenCode}</span> },
    { id: 'kitchenName', header: 'Kitchen', accessorKey: 'kitchenName', size: 200 },
    { id: 'propertyName', header: 'Property', accessorKey: 'propertyName', size: 180,
      cell: ({ row }) => row.original.propertyName ?? <span style={{ color: 'var(--text-muted)' }}>Shared</span> },
    { id: 'businessUnitName', header: 'Business Unit', accessorKey: 'businessUnitName', size: 180,
      cell: ({ row }) => row.original.businessUnitName ?? <span style={{ color: 'var(--text-muted)' }}>Not linked</span> },
    { id: 'stations', header: 'Kitchen Stations', accessorKey: 'stationNames', size: 320,
      cell: ({ row }) => (row.original.stationCount === 0
        ? <span style={{ color: 'var(--text-muted)' }}>None yet</span>
        : <span title={row.original.stationNames}><strong>{row.original.stationCount}</strong> · {row.original.stationNames}</span>) },
    { id: 'isActive', header: 'Status', accessorKey: 'isActive', size: 100,
      cell: ({ row }) => <span className={`badge badge-${row.original.isActive ? 'active' : 'inactive'}`}>{row.original.isActive ? 'Active' : 'Inactive'}</span> },
  ], []);

  const getRowActions = useCallback((record: DineKitchen): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [
      { id: 'view', label: 'View Details', icon: <IconSystem.eye size={18} />, onClick: () => { void openView(record); } },
    ];
    if (canEdit) items.push({ id: 'edit', label: 'Edit', icon: <IconSystem.edit size={18} />, onClick: () => { void openFor('edit', record); } });
    if (canAdd) items.push({ id: 'copy', label: 'Copy', icon: <IconSystem.copy size={18} />, onClick: () => { void openFor('copy', record); },
      description: 'A new kitchen with the same property and business unit; stations are not copied.' });
    return items;
  }, [canAdd, canEdit, openFor, openView]);

  const actionButtons: ActionButton[] = [
    { ...actionButtonFactory.add(openCreate, 'Add kitchen'), visible: canAdd },
    actionButtonFactory.refresh(() => { void load(); }),
    actionButtonFactory.export_(handleExport, 'Export to CSV'),
  ];

  return (
    <EnterpriseScreenShell
      title="Kitchens"
      description="Kitchens and their kitchen stations, each kitchen linked to a business unit"
      icon={<IconSystem.home size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      searchConfig={{ value: searchTerm, onChange: setSearchTerm, placeholder: 'Search kitchen or station' }}
      filterConfig={{ onClick: () => setFilterOpen((o) => !o), isActive: filterOpen, appliedCount, tooltip: 'Filters' }}
      ConfirmDialogPortal={ConfirmDialogPortal}
      trailingContent={
        <div className={`search-panel ${filterOpen ? '' : 'collapsed'}`}>
          <div className="search-panel-header">
            <h3 className="search-panel-title">Filters</h3>
            <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters"><IconSystem.x size={14} /></button>
          </div>
          <div className="search-panel-content">
            <div className="filter-section">
              <SyutSelect label="Property" options={properties.map((p) => ({ value: p.id, label: p.name }))} value={draftPropertyId}
                onChange={(v) => setDraft((d) => ({ ...d, propertyId: (v as string) || '' }))} placeholder="Every property" size="sm" isClearable
                helperText="Its kitchens and the shared ones." />
            </div>
            <div className="filter-section">
              <SyutSelect label="Status" options={[{ value: '', label: 'Active only' }, { value: 'all', label: 'Active and inactive' }]}
                value={draft.status} onChange={(v) => setDraft((d) => ({ ...d, status: ((v as string) || '') as Filters['status'] }))}
                placeholder="Active only" size="sm" isClearable />
            </div>
          </div>
          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={() => { const cleared: Filters = { propertyId: '', status: '' }; setDraft(cleared); setFilters(cleared); }}>Clear</button>
            <button className="btn btn-primary" onClick={() => setFilters({ propertyId: draftPropertyId, status: draft.status })}
              disabled={draftPropertyId === propertyId && draft.status === filters.status}>Apply</button>
          </div>
        </div>
      }
    >
      <div className="data-grid-container">
        {!ready ? (
          <GridLoadingSkeleton rows={8} columns={6} />
        ) : (
          <SyutGrid
            data={rows}
            columns={columns}
            enablePagination
            manualPagination
            totalCount={totalCount}
            currentPage={page - 1}
            pageSize={pageSize}
            onPageChange={(pageIndex) => setPage(pageIndex + 1)}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
            isLoading={loading}
            frozenRightColumns={['actions']}
            rowActions={getRowActions}
            emptyMessage={canAdd ? 'No kitchens yet. Use + to add the Main Kitchen, the Bar and any pantry.' : 'No kitchens match.'}
          />
        )}
      </div>

      {editor && (
        <DineOpsKitchenFormModal
          isOpen
          mode={editor.mode}
          record={editor.record}
          properties={properties}
          defaultPropertyId={propertyId || undefined}
          canDeleteStations={canDelete}
          tabId={tabId}
          moduleName={moduleName}
          createInstanceId={createInstanceId}
          onClose={() => setEditor(null)}
          onSaved={() => { setEditor(null); void load(); }}
          onRestoreModal={handleRestoreModal}
        />
      )}

      <DineOpsKitchenViewModal
        isOpen={!!viewing}
        record={viewing}
        canEdit={canEdit}
        canDelete={canDelete}
        tabId={tabId}
        moduleName={moduleName}
        onClose={() => setViewing(null)}
        onEdit={(r) => { void openFor('edit', r); }}
        onDelete={(r) => { void handleDelete(r); }}
        onRestoreView={(id) => { void handleRestoreView(id); }}
      />
    </EnterpriseScreenShell>
  );
};

export default DineOpsKitchensScreen;
